import React, { useState, useEffect } from 'react'
import { Wand2, Loader2, RefreshCw, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import { youtubeApi } from '../api/client'

interface StyleProfile {
  summary?: string
  niche?: string[]
  content_pillars?: string[]
  voice_tone?: string
  title_patterns?: string[]
  hook_style?: string
  catchphrases?: string[]
  format?: string
  title_exemplars?: string[]
  do?: string[]
  dont?: string[]
}

interface StyleProfileCardProps {
  /** Fired whenever the profile is created/loaded/cleared so parents can react. */
  onProfileChange?: (hasProfile: boolean) => void
}

/**
 * "My Style" — analyze the creator's connected channel into a reusable voice
 * profile the generators condition on. Shown inside the YouTube panel once a
 * channel is connected.
 */
export function StyleProfileCard({ onProfileChange }: StyleProfileCardProps) {
  const [profile, setProfile] = useState<StyleProfile | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [videosAnalyzed, setVideosAnalyzed] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await youtubeApi.getStyleProfile()
        if (cancelled) return
        setProfile(data.profile)
        setUpdatedAt(data.updated_at || null)
        setVideosAnalyzed(data.stats?.videos_analyzed ?? null)
        onProfileChange?.(!!data.profile)
      } catch {
        // Non-fatal — treat as no profile yet
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const analyze = async () => {
    setAnalyzing(true)
    setError(null)
    try {
      const data = await youtubeApi.analyzeStyle()
      setProfile(data.profile as StyleProfile)
      setVideosAnalyzed(data.stats?.videos_analyzed ?? null)
      setUpdatedAt(new Date().toISOString())
      setExpanded(true)
      onProfileChange?.(true)
    } catch (err: any) {
      const status = err?.response?.status
      const msg = err?.response?.data?.error
      if (status === 403) {
        setError('Connect your channel first, then analyze.')
      } else if (status === 404) {
        setError('No public videos found on this channel yet.')
      } else if (status === 503) {
        setError('Style analysis isn’t enabled on your account yet.')
      } else {
        setError(msg || 'Analysis failed. Please try again.')
      }
    } finally {
      setAnalyzing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 bg-gray-700/40 rounded-lg px-3 py-2.5 text-sm text-gray-400">
        <Loader2 size={14} className="animate-spin" />
        Checking your style profile…
      </div>
    )
  }

  // Empty state — invite them to train it.
  if (!profile) {
    return (
      <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-lg p-3.5">
        <div className="flex items-start gap-2.5">
          <div className="p-1.5 bg-indigo-500/20 rounded-lg mt-0.5">
            <Wand2 size={16} className="text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-semibold text-sm">Write in your style</h4>
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
              Analyze your channel so the AI learns your voice, hooks, and title
              patterns — then every generated title & description sounds like you.
            </p>
            <button
              onClick={analyze}
              disabled={analyzing}
              className="mt-2.5 inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {analyzing ? (
                <><Loader2 size={14} className="animate-spin" /> Analyzing your channel…</>
              ) : (
                <><Sparkles size={14} /> Analyze my channel</>
              )}
            </button>
            {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
          </div>
        </div>
      </div>
    )
  }

  // Trained state — show a compact summary + expandable detail.
  const chip = (label: string) => (
    <span key={label} className="inline-block px-2 py-0.5 bg-indigo-500/20 text-indigo-200 text-[11px] rounded-full border border-indigo-500/30">
      {label}
    </span>
  )

  return (
    <div className="bg-gray-800/60 border border-indigo-500/30 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3.5 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={15} className="text-indigo-300 shrink-0" />
          <span className="text-sm text-white font-medium truncate">Trained on your channel</span>
          {videosAnalyzed != null && (
            <span className="text-[11px] text-gray-500 shrink-0">· {videosAnalyzed} videos</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={analyze}
            disabled={analyzing}
            title="Re-analyze"
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
          >
            {analyzing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {profile.summary && !expanded && (
        <p className="px-3.5 pb-2.5 text-xs text-gray-400 leading-relaxed line-clamp-2">{profile.summary}</p>
      )}

      {expanded && (
        <div className="px-3.5 pb-3.5 space-y-3 border-t border-gray-700/60 pt-3">
          {profile.summary && <p className="text-xs text-gray-300 leading-relaxed">{profile.summary}</p>}
          {profile.voice_tone && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Voice</div>
              <p className="text-xs text-gray-300">{profile.voice_tone}</p>
            </div>
          )}
          {profile.niche && profile.niche.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Niche</div>
              <div className="flex flex-wrap gap-1.5">{profile.niche.map(chip)}</div>
            </div>
          )}
          {profile.content_pillars && profile.content_pillars.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Content pillars</div>
              <div className="flex flex-wrap gap-1.5">{profile.content_pillars.map(chip)}</div>
            </div>
          )}
          {profile.hook_style && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Hook style</div>
              <p className="text-xs text-gray-300">{profile.hook_style}</p>
            </div>
          )}
          {profile.title_exemplars && profile.title_exemplars.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Signature titles</div>
              <ul className="space-y-1">
                {profile.title_exemplars.slice(0, 5).map((t, i) => (
                  <li key={i} className="text-xs text-gray-400 truncate">· {t}</li>
                ))}
              </ul>
            </div>
          )}
          {updatedAt && (
            <p className="text-[11px] text-gray-600 pt-1">
              Updated {new Date(updatedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {error && <p className="px-3.5 pb-2.5 text-xs text-red-400">{error}</p>}
    </div>
  )
}
